import { Link } from "react-router-dom"
import { AlertCircle, RefreshCcw } from "lucide-react"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import { useQuery } from "@tanstack/react-query"
import { useState, useEffect, useRef } from "react"
import OrganizationSkeleton from "./organization-skeleton"

const MAX_TRIALS = 4
const RETRY_DELAY_MS = 5000

type Organization = {
  id: string
  name: string
  description?: string
  logo?: string | null
  background_image?: string | null
  category_id?: string | null
  official_color?: string | null
  active: boolean
  created_at: string
  updated_at: string
}

type ApiResponse = {
  totalItems: number
  totalPages: number
  currentPage: number
  data: Organization[]
}

const getOrgConfig = (name: string) => {
  const lowerName = name.toLowerCase()

  const theme = {
    gradient: "from-pink-50/50 to-blue-50/50",
    borderColor: "hover:border-pink-300",
    shadowColor: "hover:shadow-pink-100/60",
  }

  if (lowerName.includes("dars")) {
    return {
      ...theme,
      image: "/images/dars.png",
      href: "/organization/dars"
    }
  }

  if (lowerName.includes("shaggar")) {
    return {
      ...theme,
      image: "/images/shaggar.png",
      href: "#"
    }
  }

  if (lowerName.includes("addis ababa")) {
    return {
      ...theme,
      image: "/images/addis.png",
      href: "#"
    }
  }

  if (lowerName.includes("harari") || lowerName.includes("harar")) {
    return {
      ...theme,
      image: "/images/harar.png",
      href: "#"
    }
  }

  if (lowerName.includes("dire dawa")) {
    return {
      ...theme,
      image: "/images/diredawa.png",
      href: "#"
    }
  }

  if (lowerName.includes("ethiopian electric utility") || lowerName.includes("eeu")) {
    return {
      ...theme,
      image: "/images/eeu-new.png",
      href: "#"
    }
  }

  // Default
  return {
    ...theme,
    image: null,
    href: "#"
  }
}

async function fetchOrganizations(): Promise<Organization[]> {
  const apiUrl = import.meta.env.VITE_API_URL || import.meta.env.NEXT_PUBLIC_API_URL || ''
  const res = await fetch(`${apiUrl}/v1/organizations`, { cache: "no-store" })
  if (!res.ok) throw new Error("Failed to fetch organizations")
  const data: ApiResponse = await res.json()
  return data.data
}

export default function OrganizationPage() {
  const { t } = useTranslation()
  const [retryCount, setRetryCount] = useState(0)
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { data, isLoading, isError, refetch } = useQuery<Organization[], Error>({
    queryKey: ["organizations"],
    queryFn: fetchOrganizations,
    staleTime: 1000 * 60,
  })

  const organizationsList = (data ?? []) as Organization[]
  const hasOrganizations = organizationsList.length > 0

  useEffect(() => {
    if (hasOrganizations) setRetryCount(0)
  }, [hasOrganizations])

  useEffect(() => {
    if (isLoading || isError || hasOrganizations) return
    if (retryCount >= MAX_TRIALS - 1) return

    retryTimeoutRef.current = setTimeout(() => {
      refetch().then(() => setRetryCount((c) => c + 1))
    }, RETRY_DELAY_MS)

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
        retryTimeoutRef.current = null
      }
    }
  }, [hasOrganizations, isLoading, isError, retryCount, refetch])

  const handleOrganizationClick = (e: React.MouseEvent, href?: string) => {
    if (!href || href === "#") {
      e.preventDefault()
      toast.info(t("Coming soon!"))
    }
  }

  if (isLoading) return <OrganizationSkeleton />

  if (!hasOrganizations && retryCount < MAX_TRIALS - 1) return <OrganizationSkeleton />

  if (isError)
    return (
      <div className="px-3 md:px-4 py-12 w-full max-w-7xl mx-auto animate-fade-in-up">
        <div className="rounded-3xl bg-white shadow-sm border border-gray-100 p-18 text-center">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={32} />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">{t("failedToLoadOrganizations")}</h3>
          <p className="text-gray-500 mb-6 max-w-xs mx-auto">
            {t("errorFetchingOrganizations")}
          </p>
          <button
            onClick={() => refetch()}
            className="inline-flex flex items-center gap-2 px-5 py-2 bg-pink-600 text-white rounded-full shadow-xl shadow-pink-200 hover:shadow-2xl hover:shadow-pink-300 hover:bg-pink-700 hover:-translate-y-1 transition-all duration-300 active:scale-95"
          >
            <RefreshCcw size={16} />
            {t("retry")}
          </button>
        </div>
      </div>
    )

  // Sort organizations: priority list first
  const priorityNames = ["dars", "ethiopian electric utility"]
  const organizations: Organization[] = organizationsList.slice().sort((a, b) => {
    const lowerA = a.name.toLowerCase()
    const lowerB = b.name.toLowerCase()

    const aPriority = priorityNames.includes(lowerA)
    const bPriority = priorityNames.includes(lowerB)

    if (aPriority && !bPriority) return -1
    if (!aPriority && bPriority) return 1

    const configA = getOrgConfig(a.name)
    const configB = getOrgConfig(b.name)
    const aHasLink = configA.href && configA.href !== "#"
    const bHasLink = configB.href && configB.href !== "#"

    if (aHasLink && !bHasLink) return -1
    if (!aHasLink && bHasLink) return 1
    return 0
  })

  return (
    <>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.5s ease-out forwards;
        }
      `}</style>

      <div className="w-full mx-auto px-2 md:px-4 md:pb-20  pb-10 pt-2 font-sans  animate-fade-in-up">
        <div className="rounded-3xl bg-white shadow-sm border border-gray-100 p-3 pb-6 md:p-6 relative overflow-hidden min-h-[400px]">
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-blue-50 rounded-full blur-3xl opacity-50 pointer-events-none" />

          <div className="mb-8 md:mb-10 relative z-10">
            <h1 className="text-xl font-bold text-gray-900 tracking-tight">{t("Select Organization")}</h1>
            <p className="hidden md:block text-sm text-gray-500 mt-1.5">{t("Choose the type of facility you want to book an appointment with.")}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5 mt-2 relative z-10">
            {organizations.length === 0 ? (
              <div className="col-span-2 text-center py-12 text-gray-500">{t("No organizations available at the moment.")}</div>
            ) : (
              organizations.map((organization: Organization, index: number) => {
                const config = getOrgConfig(organization.name)

                return (
                  <Link
                    key={organization.id}
                    to={config.href || '#'}
                    onClick={(e) => handleOrganizationClick(e, config.href)}
                    style={{ animationDelay: `${index * 70}ms` }}
                    className={`group flex flex-col rounded-2xl border border-gray-100 shadow-sm bg-white ${config.borderColor} hover:shadow-2xl ${config.shadowColor} hover:-translate-y-1.5 transition-all duration-300 relative overflow-hidden animate-fade-in-up opacity-0 fill-mode-forwards`}
                  >
                    {/* Image Container */}
                    <div className={`relative w-full h-32 md:h-40 bg-gradient-to-br ${config.gradient} overflow-hidden flex items-center justify-center p-4`}>
                      {config.image ? (
                        <>
                          <div className="relative w-full h-full">
                            <img
                              src={config.image}
                              alt={organization.name}
                              className="absolute inset-0 w-full h-full object-contain group-hover:scale-105 transition-transform duration-500 ease-out"
                            />
                          </div>
                          <div className="absolute inset-0 bg-gradient-to-t from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        </>
                      ) : (
                        <div className="flex items-center justify-center">
                          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/60 backdrop-blur-sm flex items-center justify-center shadow-lg">
                            <span className="text-2xl md:text-3xl font-bold text-gray-600">
                              {organization.name.charAt(0)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-3 md:p-4 flex flex-col gap-1.5">
                      <h3 className="text-sm md:text-base font-semibold text-gray-900 group-hover:text-pink-600 transition-colors duration-300 line-clamp-1">
                        {t(organization.name)}
                      </h3>

                      <div className="flex items-center gap-1.5 mt-1">
                        <span className={`w-1.5 h-1.5 rounded-full ${config.href !== "#" ? "bg-green-500" : "bg-amber-400"}`} />
                        <span className="text-[10px] md:text-[11px] text-gray-400 font-medium">
                          {config.href !== "#" ? t("Available") : t("Coming Soon")}
                        </span>
                      </div>
                    </div>
                  </Link>
                )
              })
            )}
          </div>
        </div>
      </div>
    </>
  )
}